// Chakra imports
import { Alert, AlertIcon, Portal, Box, useDisclosure } from '@chakra-ui/react';
import Footer from 'components/footer/FooterAdmin.js';
// Layout components
import Navbar from 'components/navbar/NavbarAdmin.js';
import Sidebar from 'components/sidebar/Sidebar.js';
import { SidebarContext } from 'contexts/SidebarContext';
import React, { Suspense, useContext, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import routes from 'routes.js';
import { AuthContext } from 'context';
import ProtectedRoute from 'context/ProtectedRoute';
import { getFirstAccessiblePath } from 'utils/modulePermissions';

// Custom Chakra theme
export default function Dashboard(props) {
  const { ...rest } = props;
  const { user } = useContext(AuthContext);
  const expandedSidebarWidth = 270;
  const collapsedSidebarWidth = 100;
  // states and functions
  const [fixed] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [toggleSidebar, setToggleSidebar] = useState(false);
  const { isOpen: isMobileSidebarOpen, onOpen: onOpenMobileSidebar, onClose: onCloseMobileSidebar } = useDisclosure();
  // functions for changing the states from components
  const getRoute = () => {
    return window.location.pathname !== '/admin/full-screen-maps';
  };
  const getActiveRoute = (routes) => {
    let activeRoute = 'Default Brand Text';
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveRoute = getActiveRoute(routes[i].items);
        if (collapseActiveRoute !== activeRoute) {
          return collapseActiveRoute;
        }
      } else if (routes[i].category) {
        let categoryActiveRoute = getActiveRoute(routes[i].items);
        if (categoryActiveRoute !== activeRoute) {
          return categoryActiveRoute;
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
        ) {
          return routes[i].name;
        }
      }
    }
    return activeRoute;
  };
  const getActiveNavbar = (routes) => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveNavbar = getActiveNavbar(routes[i].items);
        if (collapseActiveNavbar !== activeNavbar) {
          return collapseActiveNavbar;
        }
      } else if (routes[i].category) {
        let categoryActiveNavbar = getActiveNavbar(routes[i].items);
        if (categoryActiveNavbar !== activeNavbar) {
          return categoryActiveNavbar;
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
        ) {
          return routes[i].secondary;
        }
      }
    }
    return activeNavbar;
  };
  const getActiveNavbarText = (routes) => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      
      if (routes[i].collapse) {
        let collapseActiveNavbar = getActiveNavbarText(routes[i].items);
        if (collapseActiveNavbar !== activeNavbar) {
          return collapseActiveNavbar;
        }
      } else if (routes[i].category) {
        let categoryActiveNavbar = getActiveNavbarText(routes[i].items);
        if (categoryActiveNavbar !== activeNavbar) {
          return categoryActiveNavbar;
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
        ) {
          return routes[i].messageNavbar;
        }
      }
    }
    return activeNavbar;
  };
  const getRoutes = (routes) => {
    return routes.map((route, key) => {
      if (route.layout === '/admin') {
        // Cada pantalla comprueba su modulo por su cuenta: un enlace directo
        // tiene que pasar el mismo filtro que el menu.
        return (
          <Route
            path={`${route.path}`}
            element={
              <ProtectedRoute moduleKey={route.moduleKey} superAdminOnly={route.superAdminOnly}>
                <Suspense fallback={null}>{route.component}</Suspense>
              </ProtectedRoute>
            }
            key={key}
          />
        );
      }
      if (route.collapse) {
        return getRoutes(route.items);
      } else {
        return null;
      }
    });
  };

  // El inicio es el primer modulo asignado: quien no tiene 'principal' no
  // puede aterrizar en el dashboard.
  const inicio = getFirstAccessiblePath(user, routes) || '/admin/sin-acceso';
  const sinAcceso = (
    <Alert status="info"><AlertIcon />No tienes módulos habilitados. Solicita acceso al administrador.</Alert>
  );

  document.documentElement.dir = 'ltr';
  document.documentElement.dir = 'ltr';
  return (
    <Box>
      <Box>
        <SidebarContext.Provider
          value={{
            toggleSidebar,
            setToggleSidebar,
          }}
        >
          <Sidebar
            routes={routes}
            isCollapsed={isSidebarCollapsed}
            setIsCollapsed={setIsSidebarCollapsed}
            isMobileOpen={isMobileSidebarOpen}
            onMobileClose={onCloseMobileSidebar}
            display="none"
            {...rest}
          />
          <Box
            minHeight="100vh"
            height="100%"
            overflowX="hidden"
            overflowY="auto"
            position="relative"
            maxHeight="100%"
            transition="all 0.35s ease"
            ms={{
              base: '0',
              lg: isSidebarCollapsed
                ? `${collapsedSidebarWidth}px`
                : `${expandedSidebarWidth}px`,
            }}
            w={{
              base: '100%',
              lg: isSidebarCollapsed
                ? `calc(100% - ${collapsedSidebarWidth}px)`
                : `calc(100% - ${expandedSidebarWidth}px)`,
            }}
            maxWidth={{
              base: '100%',
              lg: isSidebarCollapsed
                ? `calc(100% - ${collapsedSidebarWidth}px)`
                : `calc(100% - ${expandedSidebarWidth}px)`,
            }}
            minWidth="0"
            transitionDuration=".4s, .4s, .35s"
            transitionProperty="top, bottom, width"
            transitionTimingFunction="linear, linear, ease"
          >
            <Portal>
              <Box>
                <Navbar
                  w={{
                    base: 'calc(100% - 16px)',
                    md: 'calc(100% - 32px)',
                    lg: isSidebarCollapsed
                      ? 'calc(100% - 160px)'
                      : 'calc(100% - 330px)',
                  }}
                  maxWidth={{
                    base: 'calc(100% - 16px)',
                    md: 'calc(100% - 32px)',
                    lg: isSidebarCollapsed
                      ? 'calc(100% - 160px)'
                      : 'calc(100% - 330px)',
                  }}
                  onOpen={onOpenMobileSidebar}
                  onToggleSidebarCollapse={() => setIsSidebarCollapsed((prev) => !prev)}
                  logoText={'Horizon UI Dashboard PRO'}
                  brandText={getActiveRoute(routes)}
                  secondary={getActiveNavbar(routes)}
                  message={getActiveNavbarText(routes)}
                  fixed={fixed}
                  {...rest}
                />
              </Box>
            </Portal>

            {getRoute() ? (
              <Box
                mx="auto"
                w="100%"
                maxW="100%"
                p={{ base: '12px', md: '24px', xl: '30px' }}
                pe={{ base: '12px', md: '24px', xl: '30px' }}
                minH="100vh"
                pt={{ base: '110px', md: '110px' }}
              >
                <Routes>
                  {getRoutes(routes)}
                  <Route path="/sin-acceso" element={sinAcceso} />
                  <Route
                    path="/"
                    element={<Navigate to={inicio} replace />}
                  />
                </Routes>
              </Box>
            ) : null}
            <Box>
              <Footer />
            </Box>
          </Box>
        </SidebarContext.Provider>
      </Box>
    </Box>
  );
}
